import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SECTORS } from "../lib/sectors";
import { CalendarRange, Loader2, Sparkles, Check, Save } from "lucide-react";

const dayLabel = (iso) => new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

export function WeeklyPlan({ onNavigate }) {
  const [focus, setFocus] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);

  const generate = async () => {
    setLoading(true);
    setItems([]);
    try {
      const r = await api.post("/ai/weekly-plan", { focus: focus.trim() }, { timeout: 90000 });
      const its = r.data.items || [];
      setItems(its);
      const sel = {};
      its.forEach((_, i) => { sel[i] = true; });
      setSelected(sel);
      if (its.length === 0) toast.error("Nessuna idea generata, riprova.");
    } catch {
      toast.error("Errore nella generazione del piano. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const saveSelected = async () => {
    const chosen = items.filter((_, i) => selected[i]);
    if (chosen.length === 0) { toast.error("Seleziona almeno un'idea"); return; }
    setSaving(true);
    try {
      await Promise.all(chosen.map((it) => api.post("/contents", {
        sector: it.sector, title: it.title, body: it.body, type: it.type,
        date: it.date, status: "idea", completed: false,
      })));
      toast.success(`${chosen.length} contenuti salvati nel calendario!`);
      setItems([]); setSelected({});
      onNavigate && onNavigate("calendar");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // group by date
  const groups = {};
  items.forEach((it, i) => { (groups[it.date] = groups[it.date] || []).push({ ...it, _i: i }); });
  const dates = Object.keys(groups).sort();

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="plan-view">
      <div className="glass rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <CalendarRange className="h-5 w-5 text-amber-400" />
          <h3 className="font-display font-bold">Piano settimanale AI</h3>
        </div>
        <p className="text-xs text-slate-400 mb-4">L'AI propone un mini-calendario di contenuti per i prossimi 7 giorni sui 3 settori. Rivedi l'anteprima e salva solo le idee che ti piacciono.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="plan-focus-input" value={focus} onChange={(e) => setFocus(e.target.value)}
            placeholder="Focus della settimana (facoltativo): es. lancio nuovo capitolo, promo benessere…"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-amber-400/50" />
          <button data-testid="generate-plan-button" onClick={generate} disabled={loading}
            className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Genero il piano…" : "Genera piano"}
          </button>
        </div>
      </div>

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between sticky top-[73px] z-20 bg-[#0a0b10]/80 backdrop-blur py-2 -mx-1 px-1 rounded-xl">
            <p className="text-sm text-slate-400"><span className="font-bold text-white">{selectedCount}</span> idee selezionate su {items.length}</p>
            <button data-testid="save-plan-button" onClick={saveSelected} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salva selezionati
            </button>
          </div>

          <div className="space-y-6">
            {dates.map((d) => (
              <div key={d}>
                <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 capitalize">{dayLabel(d)}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {groups[d].map((it) => {
                    const S = SECTORS[it.sector];
                    const on = selected[it._i];
                    return (
                      <motion.button key={it._i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => toggle(it._i)} data-testid={`plan-item-${it._i}`}
                        className="text-left glass rounded-xl p-4 border transition-all"
                        style={{ borderLeft: `3px solid ${S.hex}`, borderColor: on ? `${S.hex}66` : "rgba(255,255,255,0.06)", background: on ? `${S.hex}10` : undefined }}>
                        <div className="flex items-start gap-3">
                          <span className="h-5 w-5 shrink-0 mt-0.5 rounded-md border grid place-items-center transition-colors"
                            style={on ? { background: S.hex, borderColor: S.hex } : { borderColor: "rgba(255,255,255,0.25)" }}>
                            {on && <Check className="h-3.5 w-3.5 text-white" />}
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md capitalize" style={{ background: `${S.hex}22`, color: S.hex }}>{S.short}</span>
                              <span className="text-[10px] text-slate-500 capitalize">{it.type}</span>
                            </div>
                            <p className="font-semibold text-sm">{it.title}</p>
                            {it.body && <p className="text-xs text-slate-400 mt-1">{it.body}</p>}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {!loading && items.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center text-slate-400">
          <CalendarRange className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Genera il tuo piano settimanale e scegli quali idee portare nel calendario.</p>
        </div>
      )}
    </div>
  );
}
